import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectAIChatEligibleDocumentsAsOptions } from 'app/store/DMSDocuments/DMSDocuments.selectors';
// Mui
import { Box, FormHelperText } from '@mui/material';
// Components
import { Button } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import Dialog from 'app/components/Dialog';

type Props = {
  open: boolean;
  onClose: (documentIds?:number[]) => void;
  documentIds?: number[];
};

export default function ChatDocumentsDialog({
  // Props
  open, onClose, documentIds
}:Props) {
  const { t } = useTranslation();

  // State
  const documentOptions = useAppSelector(selectAIChatEligibleDocumentsAsOptions);

  const [ selectedDocumentIds, setSelectedDocumentIds ] = useState<number[]>(documentIds || []);

  const handleSelectAllDocumentsClick = () => {
    if ( !documentOptions ) return;
    setSelectedDocumentIds(documentOptions.map(({ value }) => value));
  }

  const handleClose = () => {
    onClose();
  }

  const handleConfirm = () => {
    onClose(selectedDocumentIds);
  }

  const totalPages = useMemo(() => {
    if ( !documentOptions || !selectedDocumentIds.length ) return 0;
    return selectedDocumentIds.reduce((total, id) => {
      const document = documentOptions.find(({ value }) => value === id);
      if ( document ){
        total += document.props.numberOfPages;
      }
      return total;
    }, 0);
  }, [selectedDocumentIds, documentOptions]);

  const actions = (
    <Fragment>
      {selectedDocumentIds.length ? (
        <FormHelperText sx={{ pl: 4, flexGrow: 1 }}>
          {t('form.helperText.selectedDocumentsWithPages', { documents: selectedDocumentIds.length, pages: totalPages })}
        </FormHelperText>
      ) : null}
      <Button
        name="Cancel selected documents dialog"
        onClick={handleClose}
      >{t('buttons.close')}</Button>
      <Button
        name={`Apply selected documents dialog`}
        onClick={handleConfirm}
        variant="contained"
        color="primary"
      >{t('buttons.apply')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title="Documents"
      actions={actions}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <Autocomplete
          value={selectedDocumentIds}
          onChange={(value) => setSelectedDocumentIds(value as number[])}
          options={documentOptions || []}
          TextFieldProps={{
            label: t('labels.documents'),
            name: 'documents'
          }}
          groupBy={(option) => option.props.collection.name}
          renderGroup={(params) => (
            <Box key={params.key}>
              <Box sx={{
                bgcolor: 'rgb(238,238,238)',
                color: 'rgba(0,0,0,0.87)',
                fontWeight: 700,
                py: 2,
                px: 4
              }}>{params.group}</Box>
              {params.children}
            </Box>
          )}
          multiple={true}
          limitTags={3}
          filterSelectedOptions
          disableCloseOnSelect
        />
        <Button
          name="Select all documents"
          sx={{ mt: 6.5 }}
          onClick={handleSelectAllDocumentsClick}
          variant="contained"
        >{t('buttons.all')}</Button>
      </Box>
    </Dialog>
  )
}
